// frontend/src/pages/superadmin/SysAdminBilling.jsx
import React, { useState, useEffect } from 'react';
import { 
  CreditCard, Plus, CheckCircle, XCircle, Clock, 
  Trash2, Search, RefreshCw, DollarSign, Calendar, 
  AlertCircle, Building2, Wallet, TrendingUp, Package,
  ChevronDown, ChevronUp, Send, X, Loader, FileText, Download,
  Plane, ArrowUpRight, ArrowDownLeft, Activity, Link, Ban, Play
} from 'lucide-react';
import { api } from '../../contexts/authClient';
import { useAuth } from '../../contexts/AuthContext';

// ─── Toast Notification Component ────────────────────────────────────────────
const ToastNotification = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);
  
  const colors = {
    success: 'bg-green-500/20 border-green-500 text-green-400',
    error: 'bg-red-500/20 border-red-500 text-red-400',
    warning: 'bg-yellow-500/20 border-yellow-500 text-yellow-400',
    info: 'bg-blue-500/20 border-blue-500 text-blue-400'
  };
  
  return (
    <div className="fixed bottom-4 right-4 z-50 animate-toast-slide">
      <div className={`${colors[type]} border rounded-lg p-4 max-w-md shadow-xl flex items-start gap-3`}>
        {type === 'error' && <AlertCircle size={20} className="flex-shrink-0" />}
        {type === 'success' && <CheckCircle size={20} className="flex-shrink-0" />}
        {type === 'warning' && <AlertCircle size={20} className="flex-shrink-0" />}
        <div className="flex-1 text-sm whitespace-pre-wrap">{message}</div>
        <button onClick={onClose} className="text-gray-400 hover:text-white">
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

// ─── Payment Method Modal Component ─────────────────────────────────────────
const PaymentMethodModal = ({ isOpen, onClose, onConfirm, invoiceNumber }) => {
  const [paymentMethod, setPaymentMethod] = useState('wire');
  const [paymentReference, setPaymentReference] = useState('');

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (!paymentMethod) {
      alert('Please select a payment method');
      return;
    }
    onConfirm(paymentMethod, paymentReference);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[70] p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-700">
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-green-400" />
            <h3 className="text-white font-semibold">Mark Invoice as Paid</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="bg-gray-900/50 rounded-lg p-3">
            <p className="text-gray-400 text-xs">Invoice</p>
            <p className="text-white font-mono text-sm">{invoiceNumber}</p>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Payment Method *</label>
            <div className="grid grid-cols-2 gap-2">
              {['wire', 'check', 'cash', 'stripe'].map(method => (
                <button
                  key={method}
                  onClick={() => setPaymentMethod(method)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium capitalize transition ${
                    paymentMethod === method
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {method}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Payment Reference (Optional)</label>
            <input
              type="text"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              placeholder="Transaction ID, check number, etc."
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
            />
          </div>
        </div>

        <div className="flex gap-3 p-4 border-t border-gray-700">
          <button
            onClick={handleConfirm}
            className="flex-1 py-2.5 bg-green-500 hover:bg-green-600 rounded-lg text-white font-medium transition"
          >
            <CheckCircle className="w-4 h-4 inline mr-2" />
            Confirm Payment
          </button>
          <button
            onClick={onClose}
            className="flex-1 py-2.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Helper pour formater les erreurs ────────────────────────────────────────
const formatErrorMessage = (error, defaultMessage = 'An error occurred') => {
  console.error('Full error:', error);
  
  if (error.response?.data) {
    const data = error.response.data;
    
    if (typeof data === 'string') return data;
    if (data.detail) return data.detail;
    if (data.message) return data.message;
    if (data.error) return data.error;
    
    if (Array.isArray(data)) {
      return data.map(e => e.msg || e.message || JSON.stringify(e)).join('\n• ');
    }
    
    if (typeof data === 'object') {
      const errors = [];
      Object.entries(data).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          errors.push(`• ${key}: ${value.join(', ')}`);
        } else if (typeof value === 'string') {
          errors.push(`• ${key}: ${value}`);
        } else if (value?.msg) {
          errors.push(`• ${key}: ${value.msg}`);
        }
      });
      if (errors.length > 0) return errors.join('\n');
    }
  }
  
  if (error.message) return error.message;
  return defaultMessage;
};

const SysAdminBilling = () => {
  // Récupérer l'utilisateur depuis le contexte AuthContext
  const { user, getUserLevel } = useAuth();
  const userLevel = getUserLevel();
  
  const [activeTab, setActiveTab] = useState('subscriptions'); // 'subscriptions' | 'movement_fees'

  // ── States subscriptions (existants) ──────────────────────────────────────
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [airports, setAirports] = useState([]);
  const [selectedAirportPackages, setSelectedAirportPackages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [expandedPackages, setExpandedPackages] = useState(false);
  const [toast, setToast] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // ── States Movement Fees (nouveaux) ───────────────────────────────────────
  const [movLines, setMovLines] = useState([]);
  const [movStats, setMovStats] = useState(null);
  const [movLoading, setMovLoading] = useState(false);
  const [movSearch, setMovSearch] = useState('');
  const [movStatusFilter, setMovStatusFilter] = useState('all');
  const [movRunning, setMovRunning] = useState(false);
  
  const [formData, setFormData] = useState({
    airport_id: '',
    title: '',
    description: '',
    amount: '',
    period: 'monthly',
    due_date: '',
    notes: '',
    package_ids: []
  });

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // ── Load movement fees data ────────────────────────────────────────────────
  const loadMovementData = async () => {
    setMovLoading(true);
    try {
      const [linesRes, statsRes] = await Promise.all([
        api.get('/billing/line-items', { params: { limit: 200 } }),
        api.get('/billing/commission/stats'),
      ]);
      setMovLines(linesRes.data.lines || []);
      setMovStats(statsRes.data);
    } catch (err) {
      showToast(formatErrorMessage(err, 'Failed to load movement fees'), 'error');
    } finally {
      setMovLoading(false);
    }
  };

  const handleRunMonthlyBilling = async () => {
    if (!window.confirm('Run monthly billing for ALL owners with pending lines? This will create invoices and send them via Stripe.')) return;
    setMovRunning(true);
    try {
      const res = await api.post('/billing/run-monthly-billing', {});
      showToast(`✅ Monthly billing done — ${res.data.owners_processed} owner(s) processed`, 'success');
      await loadMovementData();
    } catch (err) {
      showToast(formatErrorMessage(err, 'Error running monthly billing'), 'error');
    } finally {
      setMovRunning(false);
    }
  };

  const handleExemptLine = async (lineId) => {
    try {
      await api.patch(`/billing/line-items/${lineId}/exempt`);
      showToast('Line marked as exempt', 'success');
      await loadMovementData();
    } catch (err) {
      showToast(formatErrorMessage(err, 'Error exempting line'), 'error');
    }
  };

  // Nettoyer le useEffect
  useEffect(() => {
    loadData();
    loadMovementData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [invoicesRes, statsRes, airportsRes] = await Promise.all([
        api.get('/sysadmin/billing/invoices'),
        api.get('/sysadmin/billing/stats'),
        api.get('/sysadmin/billing/airports')
      ]);
      setInvoices(invoicesRes.data.invoices || []);
      setStats(statsRes.data);
      setAirports(airportsRes.data || []);
    } catch (err) {
      showToast(formatErrorMessage(err, 'Failed to load billing data'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadAirportPackages = async (airportId) => {
    if (!airportId) {
      setSelectedAirportPackages(null);
      return;
    }
    
    try {
      const res = await api.get(`/sysadmin/billing/airports/${airportId}/subscriptions`);
      setSelectedAirportPackages(res.data);
      
      const airportName = res.data.airport_name;
      const periodText = getPeriodText(formData.period);
      
      setFormData(prev => ({
        ...prev,
        title: `${airportName} - ${periodText} Subscription`,
        package_ids: res.data.packages.map(p => p.id)
      }));
      
      calculateAmount(res.data.packages, formData.period);
    } catch (err) {
      console.error('Error loading airport packages:', err);
      showToast(formatErrorMessage(err, 'Failed to load airport packages'), 'error');
      setSelectedAirportPackages(null);
    }
  };

  const getPeriodText = (period) => {
    const periods = {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      semi_annual: 'Semi-Annual',
      yearly: 'Yearly'
    };
    return periods[period] || 'Monthly';
  };

  const getPeriodMultiplier = (period) => {
    const multipliers = {
      monthly: 1,
      quarterly: 3,
      semi_annual: 6,
      yearly: 12
    };
    return multipliers[period] || 1;
  };

  const calculateAmount = (packages, period) => {
    if (!packages || packages.length === 0) return;
    
    const multiplier = getPeriodMultiplier(period);
    let total = 0;
    
    packages.forEach(pkg => {
      if (period === 'yearly' && pkg.price_yearly && pkg.price_yearly > 0) {
        total += parseFloat(pkg.price_yearly);
      } else if (pkg.price_monthly && pkg.price_monthly > 0) {
        total += parseFloat(pkg.price_monthly) * multiplier;
      }
    });
    
    setFormData(prev => ({ ...prev, amount: total.toFixed(2) }));
  };

  const handlePeriodChange = (period) => {
    setFormData(prev => ({ ...prev, period }));
    
    if (selectedAirportPackages?.packages) {
      calculateAmount(selectedAirportPackages.packages, period);
      
      const periodText = getPeriodText(period);
      setFormData(prev => ({
        ...prev,
        title: `${selectedAirportPackages.airport_name} - ${periodText} Subscription`
      }));
    }
  };

  const handleAirportChange = (airportId) => {
    setFormData(prev => ({ 
      ...prev, 
      airport_id: airportId, 
      amount: '', 
      title: '', 
      description: '', 
      package_ids: [] 
    }));
    loadAirportPackages(airportId);
  };

  const handleCreateInvoice = async () => {
    if (!formData.title || !formData.amount || parseFloat(formData.amount) <= 0) {
      showToast('Please fill in title and amount', 'warning');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/sysadmin/billing/invoices', {
        airport_id: formData.airport_id || null,
        title: formData.title,
        description: formData.description || null,
        amount: parseFloat(formData.amount),
        period: formData.period,
        period_months: getPeriodMultiplier(formData.period),
        package_ids: formData.package_ids.length > 0 ? formData.package_ids : null,
        due_date: formData.due_date || null,
        notes: formData.notes || null
      });
      setShowModal(false);
      resetForm();
      await loadData();
      showToast('Invoice created successfully', 'success');
    } catch (err) {
      showToast(formatErrorMessage(err, 'Error creating invoice'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkPaid = (invoiceId, invoiceNumber) => {
    setSelectedInvoice({ id: invoiceId, number: invoiceNumber });
    setShowPaymentModal(true);
  };

  const handleConfirmPayment = async (paymentMethod, paymentReference) => {
    if (!selectedInvoice) return;
    
    try {
      await api.post(`/sysadmin/billing/invoices/${selectedInvoice.id}/mark-paid`, null, {
        params: { payment_method: paymentMethod, payment_reference: paymentReference }
      });
      await loadData();
      showToast(`Invoice ${selectedInvoice.number} marked as paid`, 'success');
    } catch (err) {
      showToast(formatErrorMessage(err, 'Error marking invoice as paid'), 'error');
    } finally {
      setShowPaymentModal(false);
      setSelectedInvoice(null);
    }
  };

  const handleDownloadPDF = async (invoiceId, invoiceNumber) => {
    try {
      const response = await api.get(`/sysadmin/billing/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      showToast(`Invoice ${invoiceNumber} downloaded`, 'success');
    } catch (err) {
      showToast(formatErrorMessage(err, 'Error downloading PDF'), 'error');
    }
  };

  const handleDelete = async (invoiceId, invoiceNumber, status) => {
        
    if (userLevel < 110) {
      showToast('Only SuperAdmin (level 110+) can delete invoices', 'error');
      return;
    }
    
    if (window.confirm(`Delete invoice ${invoiceNumber}? This action cannot be undone.`)) {
      try {
        await api.delete(`/sysadmin/billing/invoices/${invoiceId}`);
        await loadData();
        showToast(`Invoice ${invoiceNumber} deleted`, 'success');
      } catch (err) {
        showToast(formatErrorMessage(err, 'Error deleting invoice'), 'error');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      airport_id: '',
      title: '',
      description: '',
      amount: '',
      period: 'monthly',
      due_date: '',
      notes: '',
      package_ids: []
    });
    setSelectedAirportPackages(null);
    setExpandedPackages(false);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <span className="flex items-center gap-1 text-green-400 text-xs bg-green-500/20 px-2 py-1 rounded-full"><CheckCircle className="w-3 h-3" /> Paid</span>;
      case 'pending':
        return <span className="flex items-center gap-1 text-yellow-400 text-xs bg-yellow-500/20 px-2 py-1 rounded-full"><Clock className="w-3 h-3" /> Pending</span>;
      case 'overdue':
        return <span className="flex items-center gap-1 text-red-400 text-xs bg-red-500/20 px-2 py-1 rounded-full"><AlertCircle className="w-3 h-3" /> Overdue</span>;
      case 'cancelled':
        return <span className="flex items-center gap-1 text-gray-400 text-xs bg-gray-500/20 px-2 py-1 rounded-full"><XCircle className="w-3 h-3" /> Cancelled</span>;
      default:
        return <span className="text-xs text-gray-500">{status}</span>;
    }
  };

  const filteredInvoices = invoices.filter(inv => {
    if (statusFilter !== 'all' && inv.status !== statusFilter) return false;
    if (search && !inv.invoice_number?.toLowerCase().includes(search.toLowerCase()) &&
        !inv.airport_name?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const totalPending = stats?.summary?.pending?.total || 0;
  const totalPaid = stats?.summary?.paid?.total || 0;
  const canDelete = userLevel >= 110;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  const filteredMovLines = movLines.filter(l => {
    if (movStatusFilter !== 'all' && l.status !== movStatusFilter) return false;
    if (movSearch && !l.tail_number?.toLowerCase().includes(movSearch.toLowerCase()) &&
        !l.owner_name?.toLowerCase().includes(movSearch.toLowerCase())) return false;
    return true;
  });

  const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

  const movStatusBadge = (status) => {
    const cfg = {
      pending:         { label: 'Pending',         color: 'bg-yellow-500/20 text-yellow-400' },
      invoiced:        { label: 'Invoiced',         color: 'bg-blue-500/20 text-blue-400' },
      paid:            { label: 'Paid',             color: 'bg-green-500/20 text-green-400' },
      exempt:          { label: 'Exempt',           color: 'bg-gray-500/20 text-gray-400' },
      visitor_pending: { label: 'Visitor Pending',  color: 'bg-purple-500/20 text-purple-400' },
      stripe_pending:  { label: 'Stripe Pending',   color: 'bg-indigo-500/20 text-indigo-400' },
    };
    const { label, color } = cfg[status] || { label: status, color: 'bg-gray-500/20 text-gray-400' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>{label}</span>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-emerald-400" />
            Billing & Invoices
          </h1>
          <p className="text-gray-400 text-sm">Manage customer subscriptions, invoices and movement fees</p>
        </div>
        {activeTab === 'subscriptions' && (
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg text-white flex items-center gap-2 transition"
          >
            <Plus className="w-4 h-4" />
            Create Invoice
          </button>
        )}
        {activeTab === 'movement_fees' && (
          <button
            onClick={handleRunMonthlyBilling}
            disabled={movRunning}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 rounded-lg text-white flex items-center gap-2 transition"
          >
            {movRunning ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Play className="w-4 h-4" />}
            {movRunning ? 'Running...' : 'Run Monthly Billing'}
          </button>
        )}
      </div>

      {/* ── Tabs ── */}
      <div className="flex gap-1 bg-gray-800/50 p-1 rounded-xl border border-gray-700 w-fit">
        <button
          onClick={() => setActiveTab('subscriptions')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 ${
            activeTab === 'subscriptions'
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <CreditCard className="w-4 h-4" />
          Subscriptions
        </button>
        <button
          onClick={() => setActiveTab('movement_fees')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 ${
            activeTab === 'movement_fees'
              ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Plane className="w-4 h-4" />
          Movement Fees
          {movStats?.summary?.pending_lines > 0 && (
            <span className="bg-yellow-500 text-black text-xs rounded-full px-1.5 py-0.5 font-bold">
              {movStats.summary.pending_lines}
            </span>
          )}
        </button>
      </div>

      {/* ══════════════════════════════════════════════════════════
          TAB 1 — SUBSCRIPTIONS (contenu original)
      ══════════════════════════════════════════════════════════ */}
      {activeTab === 'subscriptions' && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-xs">Pending Amount</p>
                  <p className="text-2xl font-bold text-yellow-400 mt-1">${totalPending.toLocaleString()}</p>
                </div>
                <div className="p-2 bg-yellow-500/20 rounded-lg"><Clock className="w-5 h-5 text-yellow-400" /></div>
              </div>
              <p className="text-xs text-gray-500 mt-2">{stats?.summary?.pending?.count} invoice(s) pending</p>
            </div>
            <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-xs">Paid Amount</p>
                  <p className="text-2xl font-bold text-green-400 mt-1">${totalPaid.toLocaleString()}</p>
                </div>
                <div className="p-2 bg-green-500/20 rounded-lg"><Wallet className="w-5 h-5 text-green-400" /></div>
              </div>
              <p className="text-xs text-gray-500 mt-2">{stats?.summary?.paid?.count} invoice(s) paid</p>
            </div>
            <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-xs">Total Revenue</p>
                  <p className="text-2xl font-bold text-blue-400 mt-1">${(totalPaid + totalPending).toLocaleString()}</p>
                </div>
                <div className="p-2 bg-blue-500/20 rounded-lg"><TrendingUp className="w-5 h-5 text-blue-400" /></div>
              </div>
              <p className="text-xs text-gray-500 mt-2">{stats?.total_invoices || 0} total invoice(s)</p>
            </div>
            <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-xs">Active Airports</p>
                  <p className="text-2xl font-bold text-purple-400 mt-1">{airports.length}</p>
                </div>
                <div className="p-2 bg-purple-500/20 rounded-lg"><Building2 className="w-5 h-5 text-purple-400" /></div>
              </div>
              <p className="text-xs text-gray-500 mt-2">With active subscriptions</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-2 flex-wrap">
            {['all', 'pending', 'paid', 'overdue'].map(status => (
              <button key={status} onClick={() => setStatusFilter(status)}
                className={`px-3 py-1.5 rounded-lg text-sm transition ${statusFilter === status ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'text-gray-400 hover:text-white hover:bg-gray-700'}`}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input type="text" placeholder="Search by invoice number or airport..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm" />
          </div>

          {/* Invoices Table */}
          <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-800 border-b border-gray-700">
                  <tr className="text-gray-400 text-sm">
                    <th className="p-3 text-left">Invoice #</th>
                    <th className="p-3 text-left">Airport</th>
                    <th className="p-3 text-left">Title</th>
                    <th className="p-3 text-left">Period</th>
                    <th className="p-3 text-left">Amount</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-left">Due Date</th>
                    <th className="p-3 text-left">Created</th>
                    <th className="p-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.length === 0 ? (
                    <tr><td colSpan="9" className="text-center py-12 text-gray-500">No invoices found</td></tr>
                  ) : (
                    filteredInvoices.map(inv => (
                      <tr key={inv.id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                        <td className="p-3"><code className="text-emerald-400 text-sm font-mono">{inv.invoice_number}</code></td>
                        <td className="p-3">
                          <div>
                            <p className="text-white text-sm">{inv.airport_name || '—'}</p>
                            <p className="text-gray-500 text-xs">{inv.airport_subdomain || 'Internal'}</p>
                          </div>
                        </td>
                        <td className="p-3">
                          <p className="text-white text-sm">{inv.title}</p>
                          {inv.description && <p className="text-gray-500 text-xs truncate max-w-xs">{inv.description}</p>}
                        </td>
                        <td className="p-3"><span className="text-gray-300 text-sm capitalize">{inv.period || 'monthly'}</span></td>
                        <td className="p-3"><span className="text-white font-medium">${parseFloat(inv.amount).toLocaleString()}</span></td>
                        <td className="p-3">{getStatusBadge(inv.status)}</td>
                        <td className="p-3">{inv.due_date ? <span className="text-gray-300 text-sm">{new Date(inv.due_date).toLocaleDateString()}</span> : '—'}</td>
                        <td className="p-3"><span className="text-gray-400 text-sm">{new Date(inv.created_at).toLocaleDateString()}</span></td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <button onClick={() => handleDownloadPDF(inv.id, inv.invoice_number)}
                              className="p-1.5 hover:bg-blue-500/20 rounded-lg text-blue-400 transition" title="Download PDF">
                              <FileText className="w-4 h-4" />
                            </button>
                            {inv.status === 'pending' && (
                              <button onClick={() => handleMarkPaid(inv.id, inv.invoice_number)}
                                className="p-1.5 hover:bg-green-500/20 rounded-lg text-green-400 transition" title="Mark as paid">
                                <CheckCircle className="w-4 h-4" />
                              </button>
                            )}
                            {canDelete && (
                              <button onClick={() => handleDelete(inv.id, inv.invoice_number, inv.status)}
                                className="p-1.5 hover:bg-red-500/20 rounded-lg text-red-400 transition" title="Delete">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Refresh button */}
          <div className="flex justify-center">
            <button onClick={loadData} className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 transition">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════
          TAB 2 — MOVEMENT FEES
      ══════════════════════════════════════════════════════════ */}
      {activeTab === 'movement_fees' && (
        <>
          {/* Commission Stats */}
          {movStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-xs">Fees Collected</p>
                    <p className="text-2xl font-bold text-emerald-400 mt-1">{fmt(movStats.summary.total_fees_collected)}</p>
                  </div>
                  <div className="p-2 bg-emerald-500/20 rounded-lg"><Activity className="w-5 h-5 text-emerald-400" /></div>
                </div>
                <p className="text-xs text-gray-500 mt-2">{movStats.summary.total_lines} total lines</p>
              </div>
              <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-xs">Aerotrick Commission</p>
                    <p className="text-2xl font-bold text-blue-400 mt-1">{fmt(movStats.summary.commission_collected)}</p>
                  </div>
                  <div className="p-2 bg-blue-500/20 rounded-lg"><DollarSign className="w-5 h-5 text-blue-400" /></div>
                </div>
                <p className="text-xs text-gray-500 mt-2">{fmt(movStats.summary.commission_pending)} pending</p>
              </div>
              <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-xs">Pending Lines</p>
                    <p className="text-2xl font-bold text-yellow-400 mt-1">{movStats.summary.pending_lines}</p>
                  </div>
                  <div className="p-2 bg-yellow-500/20 rounded-lg"><Clock className="w-5 h-5 text-yellow-400" /></div>
                </div>
                <p className="text-xs text-gray-500 mt-2">{movStats.summary.paid_lines} paid</p>
              </div>
              <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-xs">Visitor Lines</p>
                    <p className="text-2xl font-bold text-purple-400 mt-1">{movStats.summary.visitor_lines}</p>
                  </div>
                  <div className="p-2 bg-purple-500/20 rounded-lg"><Plane className="w-5 h-5 text-purple-400" /></div>
                </div>
                <p className="text-xs text-gray-500 mt-2">{movStats.summary.exempt_lines} exempt</p>
              </div>
            </div>
          )}

          {/* Monthly trend */}
          {movStats?.monthly_trend?.length > 0 && (
            <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
              <h3 className="text-white text-sm font-semibold mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-blue-400" />
                Monthly Commission Trend
              </h3>
              <div className="flex items-end gap-2 h-16">
                {movStats.monthly_trend.map((m, i) => {
                  const maxVal = Math.max(...movStats.monthly_trend.map(x => x.commission), 1);
                  const h = Math.max(4, (m.commission / maxVal) * 56);
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-gray-500 text-xs">{fmt(m.commission)}</span>
                      <div className="w-full bg-blue-500 rounded-t-sm" style={{ height: `${h}px` }} title={`${m.month}: ${fmt(m.commission)}`} />
                      <span className="text-gray-600 text-xs">{new Date(m.month).toLocaleDateString('en-US', { month: 'short' })}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex gap-2 flex-wrap items-center">
            <div className="flex gap-1">
              {['all', 'pending', 'visitor_pending', 'invoiced', 'paid', 'exempt'].map(s => (
                <button key={s} onClick={() => setMovStatusFilter(s)}
                  className={`px-3 py-1.5 rounded-lg text-xs transition ${movStatusFilter === s ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'text-gray-400 hover:text-white hover:bg-gray-700'}`}>
                  {s === 'all' ? 'All' : s === 'visitor_pending' ? 'Visitor' : s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
            <div className="relative flex-1 min-w-48">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
              <input type="text" placeholder="Search tail or owner..."
                value={movSearch} onChange={(e) => setMovSearch(e.target.value)}
                className="w-full pl-8 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-xs" />
            </div>
            <button onClick={loadMovementData} className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 transition">
              <RefreshCw className={`w-4 h-4 ${movLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Movement Lines Table */}
          <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-800 border-b border-gray-700">
                  <tr className="text-gray-400 text-xs">
                    <th className="p-3 text-left">Tail</th>
                    <th className="p-3 text-left">Type</th>
                    <th className="p-3 text-left">Date</th>
                    <th className="p-3 text-left">Owner</th>
                    <th className="p-3 text-left">Fee</th>
                    <th className="p-3 text-left">Commission</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {movLoading ? (
                    <tr><td colSpan="8" className="text-center py-12 text-gray-500">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                        Loading...
                      </div>
                    </td></tr>
                  ) : filteredMovLines.length === 0 ? (
                    <tr><td colSpan="8" className="text-center py-12 text-gray-500">
                      <Plane className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      No movement lines found
                    </td></tr>
                  ) : (
                    filteredMovLines.map(l => (
                      <tr key={l.id} className="border-b border-gray-700/50 hover:bg-gray-700/20">
                        <td className="p-3">
                          <code className="text-sky-400 font-mono text-sm">{l.tail_number || '—'}</code>
                          {l.is_visitor && <span className="ml-1 text-xs text-purple-400">(visitor)</span>}
                        </td>
                        <td className="p-3">
                          <span className={`flex items-center gap-1 text-xs font-medium ${l.movement_type === 'TAKEOFF' ? 'text-blue-400' : 'text-amber-400'}`}>
                            {l.movement_type === 'TAKEOFF' ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownLeft className="w-3.5 h-3.5" />}
                            {l.movement_type === 'TAKEOFF' ? 'Kick-off' : 'Landing'}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className="text-gray-300 text-xs">
                            {l.movement_date ? new Date(l.movement_date).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className="text-gray-300 text-xs truncate max-w-24 block">{l.owner_name || <span className="text-gray-600">Unregistered</span>}</span>
                        </td>
                        <td className="p-3">
                          <span className={`text-sm font-medium ${parseFloat(l.total_amount) > 0 ? 'text-white' : 'text-gray-500'}`}>
                            {parseFloat(l.total_amount) > 0 ? fmt(l.total_amount) : 'Exempt'}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className="text-blue-400 text-xs">{fmt(l.aerotrick_fee_amt)}</span>
                          <span className="text-gray-600 text-xs ml-1">({l.aerotrick_fee_pct}%)</span>
                        </td>
                        <td className="p-3">{movStatusBadge(l.status)}</td>
                        <td className="p-3">
                          <div className="flex gap-1">
                            {['pending', 'visitor_pending'].includes(l.status) && (
                              <button onClick={() => handleExemptLine(l.id)}
                                className="p-1.5 hover:bg-gray-600 rounded text-gray-400 hover:text-white transition" title="Mark exempt">
                                <Ban className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {filteredMovLines.length > 0 && (
              <div className="px-4 py-2 border-t border-gray-700 flex justify-between items-center text-xs text-gray-500">
                <span>{filteredMovLines.length} line(s) displayed</span>
                <span>Total fees: <span className="text-white font-medium">{fmt(filteredMovLines.reduce((s, l) => s + parseFloat(l.total_amount || 0), 0))}</span></span>
              </div>
            )}
          </div>
        </>
      )}

      {/* Create Invoice Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-800 rounded-xl w-full max-w-2xl border border-gray-700 my-8">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-emerald-400" />
                Create New Invoice
              </h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Airport Selection */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Airport (optional)</label>
                <select
                  value={formData.airport_id}
                  onChange={(e) => handleAirportChange(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                >
                  <option value="">— No Airport (Internal Invoice) —</option>
                  {airports.map(airport => (
                    <option key={airport.id} value={airport.id}>
                      {airport.name} ({airport.subdomain}) - {airport.active_packages || 0} packages
                    </option>
                  ))}
                </select>
              </div>

              {/* Active Packages Display */}
              {selectedAirportPackages && selectedAirportPackages.packages?.length > 0 && (
                <div className="bg-gray-900/50 rounded-lg border border-gray-700 overflow-hidden">
                  <div 
                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-800/50"
                    onClick={() => setExpandedPackages(!expandedPackages)}
                  >
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4 text-blue-400" />
                      <span className="text-white text-sm font-medium">Active Packages</span>
                      <span className="text-xs text-gray-500">({selectedAirportPackages.packages.length})</span>
                    </div>
                    {expandedPackages ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </div>
                  
                  {expandedPackages && (
                    <div className="border-t border-gray-700 p-3 space-y-2">
                      {selectedAirportPackages.packages.map(pkg => (
                        <div key={pkg.id} className="flex items-center justify-between text-sm">
                          <div className="flex-1">
                            <p className="text-white">{pkg.display_name}</p>
                            <p className="text-gray-500 text-xs">{pkg.name}</p>
                          </div>
                          <div className="text-right">
                            {pkg.price_monthly > 0 && <p className="text-emerald-400 text-xs">${pkg.price_monthly}/mo</p>}
                            {pkg.price_yearly > 0 && <p className="text-blue-400 text-xs">${pkg.price_yearly}/yr</p>}
                          </div>
                        </div>
                      ))}
                      <div className="pt-2 mt-2 border-t border-gray-700 flex justify-between text-sm font-medium">
                        <span className="text-gray-400">Total Monthly:</span>
                        <span className="text-white">${selectedAirportPackages.total_monthly?.toFixed(2) || '0.00'}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Billing Period */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Billing Period</label>
                <div className="grid grid-cols-4 gap-2">
                  {['monthly', 'quarterly', 'semi_annual', 'yearly'].map(period => (
                    <button
                      key={period}
                      type="button"
                      onClick={() => handlePeriodChange(period)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition capitalize ${
                        formData.period === period
                          ? 'bg-emerald-500 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      {period.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Invoice Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                  placeholder="e.g., Monthly Subscription - June 2026"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="2"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                  placeholder="Additional details..."
                />
              </div>

              {/* Amount & Due Date */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Amount ($) *</label>
                  <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                    min="0" step="0.01"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Internal Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows="2"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                  placeholder="Internal notes (not visible to customer)..."
                />
              </div>
            </div>

            <div className="flex gap-3 p-4 border-t border-gray-700">
              <button
                onClick={handleCreateInvoice}
                disabled={submitting}
                className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 rounded-lg text-white font-medium disabled:opacity-50 flex items-center justify-center gap-2 transition"
              >
                {submitting ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                {submitting ? 'Creating...' : 'Create Invoice'}
              </button>
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-2.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Method Modal */}
      <PaymentMethodModal
        isOpen={showPaymentModal}
        onClose={() => { setShowPaymentModal(false); setSelectedInvoice(null); }}
        onConfirm={handleConfirmPayment}
        invoiceNumber={selectedInvoice?.number}
      />

      {/* Toast Notification */}
      {toast && (
        <ToastNotification 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}

      {/* CSS animation */}
      <style>{`
        @keyframes toast-slide {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .animate-toast-slide {
          animation: toast-slide 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default SysAdminBilling;